import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users, Plus, Edit2, Trash2, Save, X, Loader2, Clock, Calendar,
  ChevronDown, ChevronRight, Settings, CheckCircle
} from 'lucide-react';
import { uyar, hata, onayla } from '../components/GlobalModal';

// Types
interface WorkGroup {
  id: number;
  name: string;
  code: string;
  description: string;
  periodHours: number;
  periodMinutes: number;
  periodType: string;
  dayStartTime: string;
  dayEndTime: string;
  dailyWorkHours: string;
  isDefault: boolean;
  isActive: boolean;
}

interface BreakTime {
  id?: number;
  dailyWorkScheduleId?: number;
  name: string;
  startTime: string;
  endTime: string;
  breakType: string;
  deductFromWorkTime: boolean;
}

interface DailyWorkSchedule {
  id: number;
  name: string;
  code: string;
  dailyDuration: string;
  entryTime: string | null;
  exitTime: string | null;
  lateToleranceMinutes: number;
  earlyLeaveToleranceMinutes: number;
  minimumWorkDuration: string | null;
  holidayEntitlement: string | null;
  giveHolidayEntitlement: boolean;
  giveHolidayIfWorked: boolean;
  fieldName: string;
  isActive: boolean;
  breakTimes?: BreakTime[];
}

interface PayrollFieldType {
  id: number;
  code: string;
  name: string;
  type: string;
  multiplier: number;
  description: string;
  calculationType: string;
  transferCode: string;
  dailyMaxHours: string | null;
  sortOrder: number;
  isActive: boolean;
}

interface AttendancePenaltySetting {
  id: number;
  penaltyType: string;
  deductFromFieldId: number | null;
  part1FieldId: number | null;
  part2FieldId: number | null;
  part3FieldId: number | null;
  isActive: boolean;
}

interface WorkSchedule {
  id?: number;
  workGroupId: number;
  dayOfWeek: number;
  dailyWorkScheduleId: number | null;
  isHoliday: boolean;
  dailyWorkSchedule?: DailyWorkSchedule;
}

const API_BASE = '/api/workgroupapi';

const DAYS_OF_WEEK_KEYS = [
  { value: 0, labelKey: 'workGroups.days.sunday', shortKey: 'workGroups.days.sunShort' },
  { value: 1, labelKey: 'workGroups.days.monday', shortKey: 'workGroups.days.monShort' },
  { value: 2, labelKey: 'workGroups.days.tuesday', shortKey: 'workGroups.days.tueShort' },
  { value: 3, labelKey: 'workGroups.days.wednesday', shortKey: 'workGroups.days.wedShort' },
  { value: 4, labelKey: 'workGroups.days.thursday', shortKey: 'workGroups.days.thuShort' },
  { value: 5, labelKey: 'workGroups.days.friday', shortKey: 'workGroups.days.friShort' },
  { value: 6, labelKey: 'workGroups.days.saturday', shortKey: 'workGroups.days.satShort' },
];

const WorkGroups = () => {
  const { t } = useTranslation();

  // State
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'groups' | 'payroll' | 'penalties'>('groups');
  const [workGroups, setWorkGroups] = useState<WorkGroup[]>([]);
  const [dailySchedules, setDailySchedules] = useState<DailyWorkSchedule[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<WorkGroup | null>(null);
  const [weeklySchedule, setWeeklySchedule] = useState<WorkSchedule[]>([]);
  const [payrollFields, setPayrollFields] = useState<PayrollFieldType[]>([]);
  const [penaltySettings, setPenaltySettings] = useState<AttendancePenaltySetting[]>([]);
  // 2026-08-15 (kullanici talebi): Mahsuplasma parametreye baglandi.
  // Kapaliyken bu ekrandaki secimler puantaji ETKILEMEZ.
  const [mahsupAcik, setMahsupAcik] = useState<boolean>(false);
  const [mahsupKaydediliyor, setMahsupKaydediliyor] = useState(false);

  // Modal states
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showPayrollModal, setShowPayrollModal] = useState(false);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<WorkGroup | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<DailyWorkSchedule | null>(null);
  const [editingPayroll, setEditingPayroll] = useState<PayrollFieldType | null>(null);
  const [editingBreaks, setEditingBreaks] = useState<BreakTime[]>([]);
  const [currentScheduleId, setCurrentScheduleId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Form states
  const [groupForm, setGroupForm] = useState({
    name: '',
    code: '',
    description: '',
    periodHours: 225,
    periodMinutes: 0,
    periodType: '30gun',
    dayStartTime: '03:00',
    dayEndTime: '23:59',
    dailyWorkHours: '07:30',
    isDefault: false
  });

  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    code: '',
    dailyDuration: '07:30',
    entryTime: '08:00',
    exitTime: '17:00',
    lateToleranceMinutes: 5,
    earlyLeaveToleranceMinutes: 5,
    minimumWorkDuration: '',
    holidayEntitlement: '',
    giveHolidayEntitlement: false,
    giveHolidayIfWorked: false,
    fieldName: ''
  });

  const [payrollForm, setPayrollForm] = useState({
    code: '',
    name: '',
    type: 'normalcalisma',
    multiplier: 0,
    description: '',
    calculationType: 'ucretli',
    transferCode: '',
    dailyMaxHours: '',
    sortOrder: 0
  });

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [groupsRes, schedulesRes, payrollRes, penaltyRes] = await Promise.all([
        fetch(`${API_BASE}/groups`),
        fetch(`${API_BASE}/schedules`),
        fetch(`${API_BASE}/payroll-fields`),
        fetch(`${API_BASE}/penalty-settings`)
      ]);

      if (groupsRes.ok) {
        const data = await groupsRes.json();
        setWorkGroups(data.data || []);
      }
      if (schedulesRes.ok) {
        const data = await schedulesRes.json();
        setDailySchedules(data.data || []);
      }
      if (payrollRes.ok) {
        const data = await payrollRes.json();
        setPayrollFields(data.data || []);
      }
      if (penaltyRes.ok) {
        const data = await penaltyRes.json();
        setPenaltySettings(data.data || []);
        // Mahsuplasma acik mi?
        try {
          const r = await fetch('/api/settings/Payroll', { credentials: 'include' });
          const j = await r.json();
          const sozluk = j?.data ?? j ?? {};
          setMahsupAcik(String(sozluk?.PenaltyDeductionEnabled ?? 'false').toLowerCase() === 'true');
        } catch {
          setMahsupAcik(false);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWeeklySchedule = async (groupId: number) => {
    try {
      const res = await fetch(`${API_BASE}/groups/${groupId}/weekly`);
      if (res.ok) {
        const data = await res.json();
        setWeeklySchedule(data.data || []);
      }
    } catch (error) {
      console.error('Error loading weekly schedule:', error);
    }
  };

  const selectGroup = async (group: WorkGroup) => {
    setSelectedGroup(group);
    await loadWeeklySchedule(group.id);
  };

  // Group CRUD
  const handleSaveGroup = async () => {
    setSaving(true);
    try {
      const url = editingGroup
        ? `${API_BASE}/groups/${editingGroup.id}`
        : `${API_BASE}/groups`;
      const method = editingGroup ? 'PUT' : 'POST';

      const body = {
        ...groupForm,
        dailyWorkHours: groupForm.dailyWorkHours + ':00',
        dayStartTime: groupForm.dayStartTime + ':00',
        dayEndTime: groupForm.dayEndTime + ':00'
      };

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        setShowGroupModal(false);
        setEditingGroup(null);
        loadData();
      }
    } catch (error) {
      console.error('Error saving group:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (id: number) => {
    if (!(await onayla(t('workGroups.confirmDeleteGroup')))) return;
    try {
      const res = await fetch(`${API_BASE}/groups/${id}`, { method: 'DELETE' });
      if (res.ok) {
        loadData();
        if (selectedGroup?.id === id) {
          setSelectedGroup(null);
          setWeeklySchedule([]);
        }
      }
    } catch (error) {
      console.error('Error deleting group:', error);
    }
  };

  // Schedule CRUD
  const handleSaveSchedule = async () => {
    setSaving(true);
    try {
      const url = editingSchedule
        ? `${API_BASE}/schedules/${editingSchedule.id}`
        : `${API_BASE}/schedules`;
      const method = editingSchedule ? 'PUT' : 'POST';

      const body = {
        ...scheduleForm,
        dailyDuration: scheduleForm.dailyDuration + ':00',
        entryTime: scheduleForm.entryTime ? scheduleForm.entryTime + ':00' : null,
        exitTime: scheduleForm.exitTime ? scheduleForm.exitTime + ':00' : null,
        minimumWorkDuration: scheduleForm.minimumWorkDuration ? scheduleForm.minimumWorkDuration + ':00' : null,
        holidayEntitlement: scheduleForm.holidayEntitlement ? scheduleForm.holidayEntitlement + ':00' : null
      };

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        setShowScheduleModal(false);
        setEditingSchedule(null);
        loadData();
      }
    } catch (error) {
      console.error('Error saving schedule:', error);
    } finally {
      setSaving(false);
    }
  };

  // Payroll Field CRUD
  const handleSavePayroll = async () => {
    setSaving(true);
    try {
      const url = editingPayroll
        ? `${API_BASE}/payroll-fields/${editingPayroll.id}`
        : `${API_BASE}/payroll-fields`;
      const method = editingPayroll ? 'PUT' : 'POST';

      const body = {
        ...payrollForm,
        dailyMaxHours: payrollForm.dailyMaxHours ? payrollForm.dailyMaxHours + ':00' : null
      };

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        setShowPayrollModal(false);
        setEditingPayroll(null);
        loadData();
      }
    } catch (error) {
      console.error('Error saving payroll field:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayroll = async (id: number) => {
    if (!(await onayla(t('workGroups.confirmDeletePayroll')))) return;
    try {
      const res = await fetch(`${API_BASE}/payroll-fields/${id}`, { method: 'DELETE' });
      if (res.ok) loadData();
    } catch (error) {
      console.error('Error deleting payroll field:', error);
    }
  };

  // Break Times
  const openBreakModal = async (scheduleId: number) => {
    setCurrentScheduleId(scheduleId);
    try {
      const res = await fetch(`${API_BASE}/schedules/${scheduleId}`);
      if (res.ok) {
        const data = await res.json();
        setEditingBreaks(data.data?.breakTimes || []);
      }
    } catch (error) {
      console.error('Error loading break times:', error);
      setEditingBreaks([]);
    }
    setShowBreakModal(true);
  };

  const handleSaveBreaks = async () => {
    if (!currentScheduleId) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/schedules/${currentScheduleId}/breaks`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingBreaks.map(b => ({
          ...b,
          startTime: b.startTime.length === 5 ? b.startTime + ':00' : b.startTime,
          endTime: b.endTime.length === 5 ? b.endTime + ':00' : b.endTime
        })))
      });

      if (res.ok) {
        setShowBreakModal(false);
        setCurrentScheduleId(null);
        loadData();
      }
    } catch (error) {
      console.error('Error saving break times:', error);
    } finally {
      setSaving(false);
    }
  };

  const addBreak = () => {
    setEditingBreaks([...editingBreaks, {
      name: t('workGroups.newBreak'),
      startTime: '12:00',
      endTime: '13:00',
      breakType: 'lunch',
      deductFromWorkTime: true
    }]);
  };

  const removeBreak = (index: number) => {
    setEditingBreaks(editingBreaks.filter((_, i) => i !== index));
  };

  const updateBreak = (index: number, field: string, value: any) => {
    setEditingBreaks(editingBreaks.map((b, i) =>
      i === index ? { ...b, [field]: value } : b
    ));
  };

  // Penalty Settings
  const handleUpdatePenalty = async (penaltyType: string, fieldId: number | null) => {
    try {
      const res = await fetch(`${API_BASE}/penalty-settings/${penaltyType}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deductFromFieldId: fieldId })
      });
      if (res.ok) loadData();
    } catch (error) {
      console.error('Error updating penalty setting:', error);
    }
  };

  // Weekly Schedule
  const handleWeeklyScheduleChange = (dayOfWeek: number, scheduleId: number | null, isHoliday: boolean) => {
    setWeeklySchedule(prev => {
      const existing = prev.find(s => s.dayOfWeek === dayOfWeek);
      if (existing) {
        return prev.map(s => s.dayOfWeek === dayOfWeek
          ? { ...s, dailyWorkScheduleId: scheduleId, isHoliday }
          : s);
      } else {
        return [...prev, {
          workGroupId: selectedGroup!.id,
          dayOfWeek,
          dailyWorkScheduleId: scheduleId,
          isHoliday
        }];
      }
    });
  };

  const handleSaveWeeklySchedule = async () => {
    if (!selectedGroup) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/groups/${selectedGroup.id}/weekly`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(weeklySchedule)
      });

      if (res.ok) {
        uyar(t('workGroups.weeklyScheduleSaved'));
      }
    } catch (error) {
      console.error('Error saving weekly schedule:', error);
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '-';
    return timeStr.substring(0, 5);
  };

  const openEditGroup = (group: any) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
      code: group.code || '',
      description: group.description || '',
      periodHours: group.periodHours,
      periodMinutes: group.periodMinutes || 0,
      periodType: group.periodType,
      dayStartTime: formatTime(group.dayStartTime) || '03:00',
      dayEndTime: formatTime(group.dayEndTime) || '23:59',
      dailyWorkHours: formatTime(group.dailyWorkHours),
      isDefault: group.isDefault
    });
    setShowGroupModal(true);
  };

  const openNewGroup = () => {
    setEditingGroup(null);
    setGroupForm({
      name: '',
      code: '',
      description: '',
      periodHours: 225,
      periodMinutes: 0,
      periodType: '30gun',
      dayStartTime: '03:00',
      dayEndTime: '23:59',
      dailyWorkHours: '07:30',
      isDefault: false
    });
    setShowGroupModal(true);
  };

  const openEditSchedule = (schedule: DailyWorkSchedule) => {
    setEditingSchedule(schedule);
    setScheduleForm({
      name: schedule.name,
      code: schedule.code || '',
      dailyDuration: formatTime(schedule.dailyDuration),
      entryTime: formatTime(schedule.entryTime) === '-' ? '' : formatTime(schedule.entryTime),
      exitTime: formatTime(schedule.exitTime) === '-' ? '' : formatTime(schedule.exitTime),
      lateToleranceMinutes: schedule.lateToleranceMinutes,
      earlyLeaveToleranceMinutes: schedule.earlyLeaveToleranceMinutes || 5,
      minimumWorkDuration: formatTime(schedule.minimumWorkDuration) === '-' ? '' : formatTime(schedule.minimumWorkDuration),
      holidayEntitlement: formatTime(schedule.holidayEntitlement) === '-' ? '' : formatTime(schedule.holidayEntitlement),
      giveHolidayEntitlement: schedule.giveHolidayEntitlement || false,
      giveHolidayIfWorked: schedule.giveHolidayIfWorked || false,
      fieldName: schedule.fieldName || ''
    });
    setShowScheduleModal(true);
  };

  const openNewSchedule = () => {
    setEditingSchedule(null);
    setScheduleForm({
      name: '',
      code: '',
      dailyDuration: '07:30',
      entryTime: '08:00',
      exitTime: '17:00',
      lateToleranceMinutes: 5,
      earlyLeaveToleranceMinutes: 5,
      minimumWorkDuration: '',
      holidayEntitlement: '',
      giveHolidayEntitlement: false,
      giveHolidayIfWorked: false,
      fieldName: ''
    });
    setShowScheduleModal(true);
  };

  const openEditPayroll = (field: PayrollFieldType) => {
    setEditingPayroll(field);
    setPayrollForm({
      code: field.code,
      name: field.name,
      type: field.type || 'normalcalisma',
      multiplier: field.multiplier,
      description: field.description || '',
      calculationType: field.calculationType || 'ucretli',
      transferCode: field.transferCode || '',
      dailyMaxHours: formatTime(field.dailyMaxHours) === '-' ? '' : formatTime(field.dailyMaxHours),
      sortOrder: field.sortOrder
    });
    setShowPayrollModal(true);
  };

  const openNewPayroll = () => {
    setEditingPayroll(null);
    setPayrollForm({
      code: '',
      name: '',
      type: 'normalcalisma',
      multiplier: 0,
      description: '',
      calculationType: 'ucretli',
      transferCode: '',
      dailyMaxHours: '',
      sortOrder: payrollFields.length + 1
    });
    setShowPayrollModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 size={48} className="text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">{t('workGroups.title')}</h1>
          <p className="text-slate-500 text-sm font-medium">{t('workGroups.subtitle')}</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 bg-slate-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('groups')}
          className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
            activeTab === 'groups' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {t('workGroups.tabGroups')}
        </button>
        <button
          onClick={() => setActiveTab('payroll')}
          className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
            activeTab === 'payroll' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {t('workGroups.tabPayroll')}
        </button>
        <button
          onClick={() => setActiveTab('penalties')}
          className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
            activeTab === 'penalties' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {t('workGroups.tabPenalties')}
        </button>
      </div>

      {/* Tab: Groups */}
      {activeTab === 'groups' && (
      <>
      <div className="flex justify-end gap-3">
        <button
          onClick={openNewSchedule}
          className="bg-white border border-slate-200 text-slate-700 px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-slate-50 shadow-sm"
        >
          <Clock size={18} /> {t('workGroups.newSchedule')}
        </button>
        <button
          onClick={openNewGroup}
          className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-indigo-700 shadow-xl shadow-indigo-100"
        >
          <Plus size={18} /> {t('workGroups.newGroup')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Groups List */}
        <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="font-black text-slate-800">{t('workGroups.groups')}</h2>
          </div>
          <div className="divide-y divide-slate-50">
            {workGroups.map(group => (
              <div
                key={group.id}
                onClick={() => selectGroup(group)}
                className={`p-4 cursor-pointer transition-all hover:bg-slate-50 ${
                  selectedGroup?.id === group.id ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-800">{group.name}</span>
                      {group.isDefault && (
                        <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">
                          {t('workGroups.default')}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {group.periodHours}:{group.periodMinutes.toString().padStart(2, '0')} {t('workGroups.hoursPerMonth')} |
                      {t('workGroups.daily')}: {formatTime(group.dailyWorkHours)}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditGroup(group); }}
                      className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.id); }}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {workGroups.length === 0 && (
              <div className="p-8 text-center text-slate-400">
                {t('workGroups.noGroupsDefined')}
              </div>
            )}
          </div>
        </div>

        {/* Weekly Schedule */}
        <div className="lg:col-span-2 bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
            <h2 className="font-black text-slate-800">
              {selectedGroup ? `${selectedGroup.name} - ${t('workGroups.weeklyPlan')}` : t('workGroups.weeklyWorkPlan')}
            </h2>
            {selectedGroup && (
              <button
                onClick={handleSaveWeeklySchedule}
                disabled={saving}
                className="bg-green-600 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {t('workGroups.save')}
              </button>
            )}
          </div>

          {selectedGroup ? (
            <div className="p-6">
              <div className="grid grid-cols-7 gap-2">
                {DAYS_OF_WEEK_KEYS.map(day => {
                  const schedule = weeklySchedule.find(s => s.dayOfWeek === day.value);
                  const isHoliday = schedule?.isHoliday ?? (day.value === 0 || day.value === 6);

                  return (
                    <div key={day.value} className="text-center">
                      <div className={`font-bold text-xs mb-2 ${isHoliday ? 'text-red-600' : 'text-slate-600'}`}>
                        {t(day.shortKey)}
                      </div>
                      <select
                        value={schedule?.dailyWorkScheduleId || ''}
                        onChange={(e) => handleWeeklyScheduleChange(
                          day.value,
                          e.target.value ? parseInt(e.target.value) : null,
                          isHoliday
                        )}
                        className={`w-full text-xs p-2 border rounded-lg ${
                          isHoliday ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'
                        }`}
                      >
                        <option value="">-</option>
                        {dailySchedules.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                      <label className="flex items-center justify-center gap-1 mt-2 text-[10px] text-slate-500">
                        <input
                          type="checkbox"
                          checked={isHoliday}
                          onChange={(e) => handleWeeklyScheduleChange(
                            day.value,
                            schedule?.dailyWorkScheduleId || null,
                            e.target.checked
                          )}
                          className="rounded"
                        />
                        {t('workGroups.holiday')}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="p-12 text-center text-slate-400">
              <Calendar size={48} className="mx-auto mb-4 text-slate-200" />
              <p>{t('workGroups.selectGroupToView')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Daily Schedules */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="font-black text-slate-800">{t('workGroups.dailySchedules')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50">
                <th className="py-3 px-4">{t('workGroups.name')}</th>
                <th className="py-3 px-4">{t('workGroups.code')}</th>
                <th className="py-3 px-4 text-center">{t('workGroups.duration')}</th>
                <th className="py-3 px-4 text-center">{t('workGroups.entry')}</th>
                <th className="py-3 px-4 text-center">{t('workGroups.exit')}</th>
                <th className="py-3 px-4 text-center">{t('workGroups.lateTolerance')}/{t('workGroups.earlyTolerance')}</th>
                <th className="py-3 px-4">{t('workGroups.field')}</th>
                <th className="py-3 px-4 text-center">{t('workGroups.break')}</th>
                <th className="py-3 px-4 text-right">{t('workGroups.action')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {dailySchedules.map(schedule => (
                <tr key={schedule.id} className="hover:bg-slate-50/50">
                  <td className="py-3 px-4 font-bold text-slate-800">{schedule.name}</td>
                  <td className="py-3 px-4 text-slate-500 font-mono text-sm">{schedule.code}</td>
                  <td className="py-3 px-4 text-center font-bold">{formatTime(schedule.dailyDuration)}</td>
                  <td className="py-3 px-4 text-center text-green-600">{formatTime(schedule.entryTime)}</td>
                  <td className="py-3 px-4 text-center text-blue-600">{formatTime(schedule.exitTime)}</td>
                  <td className="py-3 px-4 text-center text-xs">
                    <span className="text-orange-600">{schedule.lateToleranceMinutes}</span>/
                    <span className="text-purple-600">{schedule.earlyLeaveToleranceMinutes || 5}</span> {t('workGroups.min')}
                  </td>
                  <td className="py-3 px-4 text-slate-500">{schedule.fieldName}</td>
                  <td className="py-3 px-4 text-center">
                    <button
                      onClick={() => openBreakModal(schedule.id)}
                      className="px-2 py-1 text-xs bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100"
                    >
                      {schedule.breakTimes?.length || 0} {t('workGroups.breaks')}
                    </button>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => openEditSchedule(schedule)}
                      className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                    >
                      <Edit2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {/* Tab: Payroll Fields */}
      {activeTab === 'payroll' && (
        <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
            <h2 className="font-black text-slate-800">{t('workGroups.payrollFieldTypes')}</h2>
            <button
              onClick={openNewPayroll}
              className="bg-indigo-600 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-indigo-700"
            >
              <Plus size={16} /> {t('workGroups.newField')}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50">
                  <th className="py-3 px-4">{t('workGroups.code')}</th>
                  <th className="py-3 px-4">{t('workGroups.name')}</th>
                  <th className="py-3 px-4">{t('workGroups.type')}</th>
                  <th className="py-3 px-4 text-center">{t('workGroups.multiplier')}</th>
                  <th className="py-3 px-4">{t('workGroups.calculation')}</th>
                  <th className="py-3 px-4">{t('workGroups.transferCode')}</th>
                  <th className="py-3 px-4 text-center">{t('workGroups.order')}</th>
                  <th className="py-3 px-4 text-right">{t('workGroups.action')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {payrollFields.map(field => (
                  <tr key={field.id} className="hover:bg-slate-50/50">
                    <td className="py-3 px-4 font-mono font-bold text-indigo-600">{field.code}</td>
                    <td className="py-3 px-4 font-bold text-slate-800">{field.name}</td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-1 rounded ${
                        field.type === 'fazlamesai' ? 'bg-orange-100 text-orange-700' :
                        field.type === 'normalcalisma' ? 'bg-green-100 text-green-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {field.type === 'fazlamesai' ? t('workGroups.overtime') :
                         field.type === 'normalcalisma' ? t('workGroups.normal') : field.type}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center font-bold">
                      {field.multiplier > 0 ? `%${field.multiplier}` : '-'}
                    </td>
                    <td className="py-3 px-4 text-slate-500 capitalize">{field.calculationType}</td>
                    <td className="py-3 px-4 font-mono text-sm text-slate-500">{field.transferCode || '-'}</td>
                    <td className="py-3 px-4 text-center">{field.sortOrder}</td>
                    <td className="py-3 px-4 text-right flex gap-1 justify-end">
                      <button
                        onClick={() => openEditPayroll(field)}
                        className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDeletePayroll(field.id)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: Penalty Settings */}
      {activeTab === 'penalties' && (
        <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-black text-slate-800">Mahsuplaşma Ayarları</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Geç kalma, erken çıkma ve eksik süre hangi bordro kaleminden mahsup edilsin.
                </p>
              </div>

              {/* 2026-08-15 (kullanici talebi): ac/kapa anahtari */}
              <button
                type="button"
                onClick={async () => {
                  const yeni = !mahsupAcik;
                  setMahsupKaydediliyor(true);
                  try {
                    const r = await fetch('/api/settings/Payroll/PenaltyDeductionEnabled', {
                      method: 'PUT',
                      credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ value: yeni ? 'true' : 'false' }),
                    });
                    if (!r.ok) throw new Error(`HTTP ${r.status}`);
                    setMahsupAcik(yeni);
                  } catch (e: any) {
                    uyar('Ayar kaydedilemedi: ' + (e?.message || 'bilinmeyen hata'));
                  } finally {
                    setMahsupKaydediliyor(false);
                  }
                }}
                disabled={mahsupKaydediliyor}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border-2 transition-all disabled:opacity-50 ${
                  mahsupAcik
                    ? 'border-green-500 bg-green-50'
                    : 'border-slate-200 bg-slate-50'
                }`}
              >
                <span className={`relative w-11 h-6 rounded-full transition-colors ${mahsupAcik ? 'bg-green-500' : 'bg-slate-300'}`}>
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${mahsupAcik ? 'left-[22px]' : 'left-0.5'}`} />
                </span>
                <span className={`text-sm font-black ${mahsupAcik ? 'text-green-700' : 'text-slate-500'}`}>
                  {mahsupAcik ? 'AÇIK' : 'KAPALI'}
                </span>
              </button>
            </div>

            {/* Yasal bilgilendirme — md.38 ile karistirilmasin */}
            <div className="mt-4 text-xs bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-slate-700 leading-relaxed">
              <strong className="text-blue-800">Bu bir ücret kesme cezası değildir.</strong>{' '}
              Burada yapılan işlem <strong>saat mahsuplaşmasıdır</strong>: geç kalınan süre,
              seçilen bordro kaleminden (örneğin Fazla Mesai %50) düşülür. Çalışılmayan sürenin
              ücretinin doğmaması yasaldır ve bir şart aramaz.
              <br />
              <span className="text-blue-800 font-bold">Dikkat:</span> Disiplin amaçlı{' '}
              <em>ücret kesme cezası</em> (İş Kanunu md.38) farklı bir şeydir — iş sözleşmesinde
              yazılı sebep gerektirir, ayda iki günlük ücreti aşamaz ve kesilen para işverene
              kalmaz, Bakanlık hesabına yatırılır. Bu ekran o amaçla kullanılamaz.
            </div>

            {!mahsupAcik && (
              <div className="mt-3 text-xs bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-slate-500">
                Mahsuplaşma <strong>kapalı</strong>. Aşağıdaki seçimler kaydedilir ancak puantajı etkilemez.
              </div>
            )}
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { type: 'DVM', labelKey: 'workGroups.penaltyAbsence', descKey: 'workGroups.penaltyAbsenceDesc', color: 'red' },
                { type: 'GEC', labelKey: 'workGroups.penaltyLate', descKey: 'workGroups.penaltyLateDesc', color: 'orange' },
                { type: 'ERK', labelKey: 'workGroups.penaltyEarlyLeave', descKey: 'workGroups.penaltyEarlyLeaveDesc', color: 'purple' },
                { type: 'EKS', labelKey: 'workGroups.penaltyShortDuration', descKey: 'workGroups.penaltyShortDurationDesc', color: 'blue' }
              ].map(penalty => {
                const setting = penaltySettings.find(s => s.penaltyType === penalty.type);
                return (
                  <div key={penalty.type} className={`p-4 rounded-2xl border-2 border-${penalty.color}-100 bg-${penalty.color}-50/30`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-12 h-12 rounded-xl bg-${penalty.color}-500 text-white flex items-center justify-center font-black`}>
                        {penalty.type}
                      </div>
                      <div>
                        <div className="font-bold text-slate-800">{t(penalty.labelKey)}</div>
                        <div className="text-xs text-slate-500">{t(penalty.descKey)}</div>
                      </div>
                    </div>
                    <select
                      value={setting?.deductFromFieldId || ''}
                      onChange={(e) => handleUpdatePenalty(penalty.type, e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl"
                    >
                      <option value="">{t('workGroups.noDeduction')}</option>
                      {payrollFields.map(f => (
                        <option key={f.id} value={f.id}>{f.code} - {f.name}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Group Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <h2 className="text-xl font-black text-slate-800">
                {editingGroup ? t('workGroups.editGroup') : t('workGroups.newGroup')}
              </h2>
              <button onClick={() => setShowGroupModal(false)} className="p-2 hover:bg-slate-100 rounded-xl">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.groupName')} *</label>
                <input
                  type="text"
                  value={groupForm.name}
                  onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  placeholder={t('workGroups.groupNamePlaceholder')}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.code')}</label>
                  <input
                    type="text"
                    value={groupForm.code}
                    onChange={(e) => setGroupForm({ ...groupForm, code: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                    placeholder="GEN"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.periodType')}</label>
                  <select
                    value={groupForm.periodType}
                    onChange={(e) => setGroupForm({ ...groupForm, periodType: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="30gun">{t('workGroups.period30Days')}</option>
                    <option value="28gun">{t('workGroups.period28Days')}</option>
                    <option value="haftalik">{t('workGroups.periodWeekly')}</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.periodHours')}</label>
                  <input
                    type="number"
                    value={groupForm.periodHours}
                    onChange={(e) => setGroupForm({ ...groupForm, periodHours: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.periodMinutes')}</label>
                  <input
                    type="number"
                    value={groupForm.periodMinutes}
                    onChange={(e) => setGroupForm({ ...groupForm, periodMinutes: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                    max={59}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.dailyWork')}</label>
                  <input
                    type="time"
                    value={groupForm.dailyWorkHours}
                    onChange={(e) => setGroupForm({ ...groupForm, dailyWorkHours: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.dayStart')}</label>
                  <input
                    type="time"
                    value={groupForm.dayStartTime}
                    onChange={(e) => setGroupForm({ ...groupForm, dayStartTime: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.dayEnd')}</label>
                  <input
                    type="time"
                    value={groupForm.dayEndTime}
                    onChange={(e) => setGroupForm({ ...groupForm, dayEndTime: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.description')}</label>
                <textarea
                  value={groupForm.description}
                  onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  rows={2}
                />
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={groupForm.isDefault}
                  onChange={(e) => setGroupForm({ ...groupForm, isDefault: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">{t('workGroups.defaultGroup')}</span>
              </label>
            </div>
            <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3">
              <button
                onClick={() => setShowGroupModal(false)}
                className="flex-1 py-3 border border-slate-200 rounded-xl font-bold text-slate-600 hover:bg-white"
              >
                {t('workGroups.cancel')}
              </button>
              <button
                onClick={handleSaveGroup}
                disabled={saving || !groupForm.name}
                className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                {t('workGroups.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <h2 className="text-xl font-black text-slate-800">
                {editingSchedule ? t('workGroups.editSchedule') : t('workGroups.newSchedule')}
              </h2>
              <button onClick={() => setShowScheduleModal(false)} className="p-2 hover:bg-slate-100 rounded-xl">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.scheduleName')} *</label>
                  <input
                    type="text"
                    value={scheduleForm.name}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                    placeholder={t('workGroups.scheduleNamePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.code')}</label>
                  <input
                    type="text"
                    value={scheduleForm.code}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, code: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                    placeholder="GHI"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.dailyDuration')}</label>
                  <input
                    type="time"
                    value={scheduleForm.dailyDuration}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, dailyDuration: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.entryTime')}</label>
                  <input
                    type="time"
                    value={scheduleForm.entryTime}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, entryTime: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.exitTime')}</label>
                  <input
                    type="time"
                    value={scheduleForm.exitTime}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, exitTime: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.lateToleranceMin')}</label>
                  <input
                    type="number"
                    value={scheduleForm.lateToleranceMinutes}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, lateToleranceMinutes: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.earlyLeaveToleranceMin')}</label>
                  <input
                    type="number"
                    value={scheduleForm.earlyLeaveToleranceMinutes}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, earlyLeaveToleranceMinutes: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.minWorkDuration')}</label>
                  <input
                    type="time"
                    value={scheduleForm.minimumWorkDuration}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, minimumWorkDuration: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.holidayEntitlement')}</label>
                  <input
                    type="time"
                    value={scheduleForm.holidayEntitlement}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, holidayEntitlement: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
                  <input
                    type="checkbox"
                    checked={scheduleForm.giveHolidayEntitlement}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, giveHolidayEntitlement: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">{t('workGroups.giveHolidayEntitlement')}</span>
                </label>
                <label className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
                  <input
                    type="checkbox"
                    checked={scheduleForm.giveHolidayIfWorked}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, giveHolidayIfWorked: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">{t('workGroups.holidayIfWorked')}</span>
                </label>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.fieldName')}</label>
                <input
                  type="text"
                  value={scheduleForm.fieldName}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, fieldName: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  placeholder={t('workGroups.normalWork')}
                />
              </div>
            </div>
            <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3">
              <button
                onClick={() => setShowScheduleModal(false)}
                className="flex-1 py-3 border border-slate-200 rounded-xl font-bold text-slate-600 hover:bg-white"
              >
                {t('workGroups.cancel')}
              </button>
              <button
                onClick={handleSaveSchedule}
                disabled={saving || !scheduleForm.name}
                className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                {t('workGroups.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payroll Modal */}
      {showPayrollModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <h2 className="text-xl font-black text-slate-800">
                {editingPayroll ? t('workGroups.editPayrollField') : t('workGroups.newPayrollField')}
              </h2>
              <button onClick={() => setShowPayrollModal(false)} className="p-2 hover:bg-slate-100 rounded-xl">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.code')} *</label>
                  <input
                    type="text"
                    value={payrollForm.code}
                    onChange={(e) => setPayrollForm({ ...payrollForm, code: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                    placeholder="N.C"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.name')} *</label>
                  <input
                    type="text"
                    value={payrollForm.name}
                    onChange={(e) => setPayrollForm({ ...payrollForm, name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                    placeholder={t('workGroups.normalWork')}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.type')}</label>
                  <select
                    value={payrollForm.type}
                    onChange={(e) => setPayrollForm({ ...payrollForm, type: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="normalcalisma">{t('workGroups.normalWork')}</option>
                    <option value="fazlamesai">{t('workGroups.overtime')}</option>
                    <option value="diger">{t('workGroups.other')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.multiplierPercent')}</label>
                  <input
                    type="number"
                    value={payrollForm.multiplier}
                    onChange={(e) => setPayrollForm({ ...payrollForm, multiplier: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                    placeholder="50"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.calculationType')}</label>
                  <select
                    value={payrollForm.calculationType}
                    onChange={(e) => setPayrollForm({ ...payrollForm, calculationType: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="ucretli">{t('workGroups.paid')}</option>
                    <option value="ucretsiz">{t('workGroups.unpaid')}</option>
                    <option value="kesinti">{t('workGroups.deduction')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.transferCode')}</label>
                  <input
                    type="text"
                    value={payrollForm.transferCode}
                    onChange={(e) => setPayrollForm({ ...payrollForm, transferCode: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.dailyMaxHours')}</label>
                  <input
                    type="time"
                    value={payrollForm.dailyMaxHours}
                    onChange={(e) => setPayrollForm({ ...payrollForm, dailyMaxHours: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.sortOrder')}</label>
                  <input
                    type="number"
                    value={payrollForm.sortOrder}
                    onChange={(e) => setPayrollForm({ ...payrollForm, sortOrder: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">{t('workGroups.description')}</label>
                <input
                  type="text"
                  value={payrollForm.description}
                  onChange={(e) => setPayrollForm({ ...payrollForm, description: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>
            </div>
            <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3">
              <button
                onClick={() => setShowPayrollModal(false)}
                className="flex-1 py-3 border border-slate-200 rounded-xl font-bold text-slate-600 hover:bg-white"
              >
                {t('workGroups.cancel')}
              </button>
              <button
                onClick={handleSavePayroll}
                disabled={saving || !payrollForm.code || !payrollForm.name}
                className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                {t('workGroups.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Break Times Modal */}
      {showBreakModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <h2 className="text-xl font-black text-slate-800">{t('workGroups.breakTimes')}</h2>
              <button onClick={() => setShowBreakModal(false)} className="p-2 hover:bg-slate-100 rounded-xl">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
              {editingBreaks.map((brk, index) => (
                <div key={index} className="p-4 bg-slate-50 rounded-xl space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-sm text-slate-700">{t('workGroups.break')} {index + 1}</span>
                    <button
                      onClick={() => removeBreak(index)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 mb-1">{t('workGroups.name')}</label>
                      <input
                        type="text"
                        value={brk.name}
                        onChange={(e) => updateBreak(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 mb-1">{t('workGroups.start')}</label>
                      <input
                        type="time"
                        value={brk.startTime.substring(0, 5)}
                        onChange={(e) => updateBreak(index, 'startTime', e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 mb-1">{t('workGroups.end')}</label>
                      <input
                        type="time"
                        value={brk.endTime.substring(0, 5)}
                        onChange={(e) => updateBreak(index, 'endTime', e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={brk.breakType}
                      onChange={(e) => updateBreak(index, 'breakType', e.target.value)}
                      className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                    >
                      <option value="lunch">{t('workGroups.lunch')}</option>
                      <option value="tea">{t('workGroups.teaBreak')}</option>
                      <option value="other">{t('workGroups.other')}</option>
                    </select>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={brk.deductFromWorkTime}
                        onChange={(e) => updateBreak(index, 'deductFromWorkTime', e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-xs">{t('workGroups.deductFromWork')}</span>
                    </label>
                  </div>
                </div>
              ))}
              <button
                onClick={addBreak}
                className="w-full py-3 border-2 border-dashed border-slate-300 text-slate-500 rounded-xl font-bold flex items-center justify-center gap-2 hover:border-indigo-400 hover:text-indigo-600"
              >
                <Plus size={18} /> {t('workGroups.addBreak')}
              </button>
            </div>
            <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3">
              <button
                onClick={() => setShowBreakModal(false)}
                className="flex-1 py-3 border border-slate-200 rounded-xl font-bold text-slate-600 hover:bg-white"
              >
                {t('workGroups.cancel')}
              </button>
              <button
                onClick={handleSaveBreaks}
                disabled={saving}
                className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                {t('workGroups.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkGroups;
